import { z } from "zod";
import { stripLoneSurrogates } from "./textSanitize";

// La couche fichier commune : la forme d'un fichier stocké, sa nature (PDF,
// tableur, archive…) et le type MIME qu'on lui prête d'après son nom.
//
// Partagée par le node `file` (un fichier, `values.file`), le champ `file`
// des custom nodes (une liste) et tout ce qui les lit : indexation, lecture
// par l'agent, aperçus, icônes. Pur, sans contexte Convex ni DOM : le front
// l'importe tel quel.
//
// Les nodes audio, vidéo et image gardent leur propre forme : ce sont des
// lecteurs, avec des réglages à eux (boucle, pochette, galerie…).

// ── La forme stockée ────────────────────────────────────────────────────

/** Le plus long nom qu'une `FileRef` accepte. */
export const MAX_FILE_NAME_LENGTH = 255;

/**
 * Un fichier déjà téléversé sur R2. Les références passent par la synchro R2
 * des nodeDatas (cf. `lib/r2Keys`) : supprimer le node ou la ligne libère
 * ses fichiers.
 */
export const fileRefSchema = z.object({
  key: z.string().min(1).max(1024).describe("The storage key of the file."),
  // URL publique renvoyée par l'upload : de quoi afficher sans requête.
  url: z.string().url().max(2048).describe("The public URL of the file."),
  name: z.string().min(1).max(MAX_FILE_NAME_LENGTH).describe("The display filename."),
  mime: z.string().min(1).max(255).describe("The MIME type of the file."),
  size: z.number().int().nonnegative().describe("The file size in bytes."),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});

export type FileRef = z.infer<typeof fileRefSchema>;

// ── La nature d'un fichier ──────────────────────────────────────────────

/**
 * Ce qu'est un fichier, du point de vue de l'app : ce qui décide de son
 * icône, de sa visionneuse et de son indexation. Plus fin que les catégories
 * d'un champ `file` (`FILE_CATEGORIES`), qui ne servent qu'à filtrer ce
 * qu'un champ accepte.
 */
export const FILE_KINDS = [
  "pdf",
  "image",
  "audio",
  "video",
  "word",
  "spreadsheet",
  "presentation",
  "text",
  "archive",
  "other",
] as const;
export type FileKind = (typeof FILE_KINDS)[number];

const KIND_BY_MIME: Record<string, FileKind> = {
  "application/pdf": "pdf",
  "application/msword": "word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "word",
  "application/vnd.oasis.opendocument.text": "word",
  "application/rtf": "word",
  "application/vnd.ms-excel": "spreadsheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "spreadsheet",
  "application/vnd.oasis.opendocument.spreadsheet": "spreadsheet",
  "text/csv": "spreadsheet",
  "application/vnd.ms-powerpoint": "presentation",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "presentation",
  "application/vnd.oasis.opendocument.presentation": "presentation",
  "text/plain": "text",
  "text/markdown": "text",
  "application/json": "text",
  "application/xml": "text",
  "text/xml": "text",
  "application/zip": "archive",
  "application/x-zip-compressed": "archive",
  "application/x-rar-compressed": "archive",
  "application/vnd.rar": "archive",
  "application/x-7z-compressed": "archive",
  "application/gzip": "archive",
  "application/x-tar": "archive",
};

const MIME_FAMILY_KINDS: Array<[prefix: string, kind: FileKind]> = [
  ["image/", "image"],
  ["audio/", "audio"],
  ["video/", "video"],
];

/**
 * La nature d'après l'extension, y compris pour les formats que l'upload
 * refuse (SVG) ou que les navigateurs ne lisent pas (`.mkv`) : c'est ce qui
 * route un fichier glissé vers le bon node, avant même l'envoi.
 */
const KIND_BY_EXTENSION: Record<string, FileKind> = {
  pdf: "pdf",
  jpg: "image",
  jpeg: "image",
  png: "image",
  gif: "image",
  webp: "image",
  avif: "image",
  svg: "image",
  bmp: "image",
  ico: "image",
  mp3: "audio",
  m4a: "audio",
  wav: "audio",
  ogg: "audio",
  opus: "audio",
  flac: "audio",
  aac: "audio",
  mp4: "video",
  m4v: "video",
  mov: "video",
  webm: "video",
  ogv: "video",
  mkv: "video",
  avi: "video",
  wmv: "video",
  flv: "video",
  mpg: "video",
  mpeg: "video",
  "3gp": "video",
  doc: "word",
  docx: "word",
  odt: "word",
  rtf: "word",
  xls: "spreadsheet",
  xlsx: "spreadsheet",
  ods: "spreadsheet",
  csv: "spreadsheet",
  ppt: "presentation",
  pptx: "presentation",
  odp: "presentation",
  txt: "text",
  md: "text",
  markdown: "text",
  json: "text",
  xml: "text",
  zip: "archive",
  rar: "archive",
  "7z": "archive",
  gz: "archive",
  tar: "archive",
};

/**
 * `rapport.final.PDF` -> `pdf` ; chaîne vide sans extension. Prend un nom de
 * fichier ou le chemin d'une URL (`new URL(…).pathname`), jamais une URL
 * entière : `#` et `?` sont des caractères permis dans un nom (`Devis #3.pdf`).
 */
export function fileExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot < 0 || dot === name.length - 1) return "";
  const slash = Math.max(name.lastIndexOf("/"), name.lastIndexOf("\\"));
  if (dot < slash) return "";
  return name.slice(dot + 1).toLowerCase();
}

/** `image/png; charset=binary` -> `image/png`. Chaîne vide si non parsable. */
export function normalizeMimeType(mime: string | undefined): string {
  return (mime ?? "").split(";")[0].trim().toLowerCase();
}

/** Un CSV, d'après son type ou son extension (il arrive annoncé `text/plain`). */
export function isCsvFile(file: { mime?: string; name: string }): boolean {
  return normalizeMimeType(file.mime) === "text/csv" || fileExtension(file.name) === "csv";
}

/**
 * La nature d'un fichier : son type MIME d'abord, puis son extension —
 * beaucoup de fichiers arrivent sans type utile (`application/octet-stream`,
 * ou vide pour un fichier glissé depuis certains explorateurs).
 */
export function fileKindOf(file: { mime?: string; name?: string }): FileKind {
  const mime = normalizeMimeType(file.mime);
  const byMime = KIND_BY_MIME[mime];
  if (byMime) return byMime;
  for (const [prefix, kind] of MIME_FAMILY_KINDS) {
    if (mime.startsWith(prefix)) return kind;
  }
  return KIND_BY_EXTENSION[fileExtension(file.name ?? "")] ?? "other";
}

// ── Le type MIME d'après le nom ─────────────────────────────────────────

/**
 * Le type MIME d'une extension, pour un fichier servi sans type utile ou
 * dont on ne connaît que le nom. Seulement des types que l'upload accepte
 * (cf. `config/uploadsConfig`) : le reste reste inconnu, donc refusé.
 */
export const MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  avif: "image/avif",
  bmp: "image/bmp",
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  wav: "audio/wav",
  ogg: "audio/ogg",
  opus: "audio/opus",
  flac: "audio/flac",
  aac: "audio/aac",
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  ogv: "video/ogg",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  wmv: "video/x-ms-wmv",
  flv: "video/x-flv",
  mpg: "video/mpeg",
  mpeg: "video/mpeg",
  "3gp": "video/3gpp",
  txt: "text/plain",
  csv: "text/csv",
  md: "text/markdown",
  markdown: "text/markdown",
  json: "application/json",
  xml: "application/xml",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  zip: "application/zip",
  rar: "application/x-rar-compressed",
  "7z": "application/x-7z-compressed",
};

/** `rapport.PDF` -> `application/pdf` ; `null` pour une extension inconnue. */
export function mimeTypeFromFilename(filename: string): string | null {
  return MIME_BY_EXTENSION[fileExtension(filename)] ?? null;
}

// ── Ce que l'app sait lire d'un fichier ─────────────────────────────────

/**
 * Le contenu qu'on sait tirer d'un fichier, au-delà de son nom :
 *  - `pages` : des pages de texte, par OCR (Mistral lit PDF, DOCX et PPTX) ;
 *  - `sheets` : des feuilles de cellules (xlsx, CSV).
 * Les anciens formats Office (doc, xls, ppt) ne se lisent pas : leur nom
 * seul est indexé.
 */
export type ReadableContent = "pages" | "sheets";

const MB = 1024 * 1024;
/** Au-delà, Mistral OCR refuse le document : son nom seul est indexé. */
export const MAX_OCR_FILE_BYTES = 50 * MB;
/** Un tableur se lit en mémoire, dans l'action comme dans l'onglet. */
export const MAX_SHEET_FILE_BYTES = 20 * MB;


/**
 * Le format lisible d'un fichier, quelle que soit sa taille : ce que les
 * visionneuses regardent (elles ont leurs propres limites, un gros PDF se
 * lit très bien sans avoir été indexé).
 */
export function fileContentFormat(file: Pick<FileRef, "mime" | "name">): ReadableContent | null {
  const mime = normalizeMimeType(file.mime);
  const is = (extension: string) =>
    mime === MIME_BY_EXTENSION[extension] || fileExtension(file.name) === extension;
  switch (fileKindOf(file)) {
    case "pdf":
      return "pages";
    case "word":
      return is("docx") ? "pages" : null;
    case "presentation":
      return is("pptx") ? "pages" : null;
    case "spreadsheet":
      return is("xlsx") || isCsvFile(file) ? "sheets" : null;
    default:
      return null;
  }
}

/**
 * Le contenu lisible d'un fichier, ou `null` : format inconnu, ou trop gros.
 * Une taille de 0 est une taille inconnue (fichier posé par l'agent) : on
 * essaie.
 */
export function readableContent(
  file: Pick<FileRef, "mime" | "name" | "size">,
): ReadableContent | null {
  const format = fileContentFormat(file);
  if (!format) return null;
  const limit = format === "pages" ? MAX_OCR_FILE_BYTES : MAX_SHEET_FILE_BYTES;
  return file.size > limit ? null : format;
}

// ── Lecture défensive ───────────────────────────────────────────────────

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
}

/**
 * Une entrée de fichier stockée, sous l'une ou l'autre des formes qu'elle a
 * prises (`name`/`mime` d'une `FileRef`, `filename`/`mimeType` des nodes
 * audio, vidéo et de l'ancien node `pdf`), en `FileRef`. Sans clé R2 (fichier
 * ancien ou externe), l'URL tient lieu de clé, comme `collectR2KeysForFields`
 * l'attend. `null` sans URL.
 */
export function toFileRef(
  file: unknown,
  fallback: { name: string; mime: string } = { name: "file", mime: "application/octet-stream" },
): FileRef | null {
  if (!file || typeof file !== "object") return null;
  const raw = file as Record<string, unknown>;
  const url = nonEmptyString(raw.url);
  if (!url) return null;
  const size = typeof raw.size === "number" && Number.isFinite(raw.size) ? raw.size : 0;
  // Couper peut trancher une paire de surrogates : le reste est assaini.
  const name = stripLoneSurrogates(
    (nonEmptyString(raw.name) ?? nonEmptyString(raw.filename) ?? fallback.name).slice(
      0,
      MAX_FILE_NAME_LENGTH,
    ),
  );
  const mime =
    nonEmptyString(raw.mime) ?? nonEmptyString(raw.mimeType) ?? mimeTypeFromFilename(name) ?? fallback.mime;
  const ref: FileRef = {
    key: nonEmptyString(raw.key) ?? url,
    url,
    name,
    mime,
    size: Math.max(0, Math.round(size)),
  };
  if (typeof raw.width === "number" && raw.width > 0) ref.width = Math.round(raw.width);
  if (typeof raw.height === "number" && raw.height > 0) ref.height = Math.round(raw.height);
  return ref;
}

/** `1536000` -> `1.5 MB`. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  const rounded = value >= 10 || unit === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}
